import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUserStore } from '../stores/StoreProvider';

const OnboardingPosition = observer(() => {
  const navigate = useNavigate();
  const userStore = useUserStore();
  const [selectedPosition, setSelectedPosition] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const positions = [
    {
      id: 'skater',
      title: 'Skater',
      description: 'Forward or Defense',
      icon: '🏒',
      color: 'bg-blue-500'
    },
    {
      id: 'goalie',
      title: 'Goalie',
      description: 'Goalkeeper',
      icon: '🥅',
      color: 'bg-green-500'
    }
  ];

  const handleSubmit = async () => {
    if (!selectedPosition) {
      toast.error('Please select a position');
      return;
    }

    setIsSubmitting(true);
    try {
      // Store position in onboarding data
      userStore.onboardingData = {
        ...userStore.onboardingData,
        position: selectedPosition,
      };
      
      console.log('Position saved to onboarding data:', selectedPosition);
      
      toast.success('Position saved!');
      navigate('/onboarding/birthday-zipcode');
    } catch (error) {
      console.error('Error saving position:', error);
      toast.error('Failed to save position. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-2xl">B</span>
              </div>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-2">
            What's your position?
          </h2>
          <p className="text-blue-100">
            This helps us customize your stats tracking
          </p>
        </div>

        {/* Position Selection */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="space-y-4">
            {positions.map((position) => (
              <button
                key={position.id}
                onClick={() => setSelectedPosition(position.id)}
                className={`w-full p-4 rounded-lg border-2 transition-all ${
                  selectedPosition === position.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 ${position.color} rounded-lg flex items-center justify-center text-white text-2xl`}>
                    {position.icon}
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-semibold text-gray-900">{position.title}</h3>
                    <p className="text-sm text-gray-600">{position.description}</p>
                  </div>
                  {selectedPosition === position.id && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 mt-8">
            <button
              onClick={() => navigate('/onboarding/role')}
              className="flex-1 flex justify-center items-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="mr-2" size={20} />
              Back
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={!selectedPosition || isSubmitting}
              className="flex-1 flex justify-center items-center px-4 py-3 border border-transparent text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  Continue
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="text-center">
          <div className="flex justify-center space-x-2">
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
          </div>
          <p className="text-white/80 text-sm mt-2">Step 4 of 6</p>
        </div>
      </div>
    </div>
  );
});

export default OnboardingPosition;
